import { Heading } from '@core/ui';
import { useEffect, useRef, useState } from 'react';
import { useTranslation } from 'react-i18next';

export const StandardMessages = ({ onSelectMessage }: { onSelectMessage: (text: string) => void }) => {
    const messageWrapper = useRef<HTMLDivElement>(null);
    const [showMessages, setShowMessages] = useState(false);
    const { t } = useTranslation();

    const keys = Array.from({ length: 9 }, (_, i) => `Chat.Standard.Message${i + 1}`);
    const standardMessages = keys
        .map((key) => ({
            key,
            value: t(key)
        }))
        .filter((answer) => answer.value); /* Filter out all keys that don't have a value */

    const checkButtonHasFocus = () => {
        if (!messageWrapper.current) return;

        const buttons = messageWrapper.current.querySelectorAll('button');
        const btnFocused = Array.from(buttons).some((btn) => btn === document.activeElement);

        setShowMessages(btnFocused);
    };

    const handleKeyDown = (e: KeyboardEvent) => {
        if (e.key === 'Control' && !showMessages) {
            setShowMessages(true);
        }

        if (e.ctrlKey) {
            const keyAsNumber = Number(e.key);
            if (keyAsNumber >= 1 && keyAsNumber <= standardMessages.length) {
                const text = standardMessages[keyAsNumber - 1];
                if (text && text.value) {
                    e.preventDefault(); /* Prevent the standard browser functionality of switching tabs */
                    handleSelectMessage(text.value);
                }
            }
        }
    };

    const handleKeyUp = (e: KeyboardEvent) => {
        if (e.key === 'Control') {
            setShowMessages(false);
        }
    };

    const handleSelectMessage = (text: string) => {
        onSelectMessage(text);
    };

    useEffect(() => {
        globalThis.document.addEventListener('keydown', handleKeyDown);
        globalThis.document.addEventListener('keyup', handleKeyUp);
        globalThis.window.addEventListener('focusin', checkButtonHasFocus);
        globalThis.window.addEventListener('focusout', checkButtonHasFocus);

        return () => {
            globalThis.document.removeEventListener('keydown', handleKeyDown);
            globalThis.document.removeEventListener('keyup', handleKeyUp);
            globalThis.window.removeEventListener('focusin', checkButtonHasFocus);
            globalThis.window.removeEventListener('focusout', checkButtonHasFocus);
        };
    }, []);

    return (
        <div
            ref={messageWrapper}
            className={`w-full max-h-full min-h-0 bg-white overflow-y-auto pointer-events-auto shadow-menu ${showMessages ? 'translate-none' : 'translate-y-full'} motion-safe:transition-transform motion-safe:duration-300`}
        >
            <div className="max-w-2xl mx-auto px-6">
                <Heading variant="h3" styleAs="h4" classNames="mt-2">
                    {t('Chat.Standard.Header')}
                </Heading>
                {standardMessages.length > 0 ? (
                    <ol className={`relative min-h-0 max-h-full flex flex-col gap-y-1 py-2`}>
                        {standardMessages.map((message, index) => (
                            <li key={message.key}>
                                <span className="bg-white border border-gray-400 rounded-lg text-sm px-1 py-0.5">Ctrl+{index + 1}</span>
                                <button
                                    className="bg-chat-own-msg hover:bg-white focus-within:bg-white border-2 border-chat-own-msg hover:border-primary focus-within:border-primary rounded-lg cursor-pointer block text-base wrap-break-word -mt-2 p-2 text-left transition-colors ease-in-out duration-200"
                                    onClick={() => handleSelectMessage(message.value)}
                                >
                                    {message.value}
                                </button>
                            </li>
                        ))}
                    </ol>
                ) : (
                    <p className="pb-2">{t('Chat.Standard.NoMessages')}</p>
                )}
            </div>
        </div>
    );
};
